import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]" data-testid="forgot-password-page">
      <div className="w-full max-w-sm bg-white border border-[#E5E7EB] p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-6 w-6 text-[#2563EB]" />
          <span className="font-heading font-extrabold text-lg">AccuSearch WPM</span>
        </div>
        {sent ? (
          <div data-testid="forgot-password-success">
            <MailCheck className="h-10 w-10 text-[#10B981] mb-4" />
            <h1 className="font-heading text-xl font-extrabold text-[#111827] mb-2">Check your inbox</h1>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              If <span className="font-mono text-xs">{email}</span> is registered, we've sent a password reset link. It expires in 1 hour.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <h1 className="font-heading text-xl font-extrabold text-[#111827] mb-1">Forgot password</h1>
            <p className="text-sm text-[#4B5563] mb-6">Enter your account email and we'll send you a reset link.</p>
            <Label htmlFor="reset-email" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Email</Label>
            <Input id="reset-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 mb-4 rounded-sm" placeholder="you@company.com" data-testid="forgot-password-email-input" />
            {error && <div className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 mb-4" data-testid="forgot-password-error">{error}</div>}
            <Button type="submit" disabled={loading}
              className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
              data-testid="forgot-password-submit-button">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        )}
        <Link to="/login" className="mt-6 flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline" data-testid="back-to-login-link">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
